'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useState, useEffect, useRef } from 'react'
import { SiteFooter } from '@/components/ui/site-footer'

const WHATSAPP_LINK = 'https://wa.me/5561999969213?text=Ol%C3%A1%2C%20tenho%20interesse%20nas%20cotas%20de%20patroc%C3%ADnio%20do%20Encontro%20de%20Neg%C3%B3cios.'

const VIDEO_URLS = [
  'https://antoniofogaca.com.br/wp-content/uploads/2026/03/10-1.mp4',
  'https://antoniofogaca.com.br/wp-content/uploads/2026/03/1202-13.mp4',
  'https://antoniofogaca.com.br/wp-content/uploads/2026/03/12022.mp4',
]

const TIERS = [
  {
    name: 'Diamante',
    price: '150',
    color: 'from-cyan-300 to-cyan-500',
    textColor: 'text-cyan-400',
    borderColor: 'border-cyan-500/30',
    bgGlow: 'shadow-cyan-500/10',
    featured: true,
    benefits: [
      '4 eventos presenciais (todas as edicoes)',
      '24 posts/ano na conta de Antonio (879k alcance/mes)',
      'Logo em todos os materiais e palco',
      'Palestra de 15min em 2 eventos',
      'Email marketing para +500 empresarios',
      'Mencao em stories semanais',
      'Stand exclusivo nos eventos',
      'Acesso VIP a todas as edicoes',
    ],
  },
  {
    name: 'Ouro',
    price: '100',
    color: 'from-amber-400 to-amber-600',
    textColor: 'text-amber-400',
    borderColor: 'border-amber-500/30',
    bgGlow: 'shadow-amber-500/10',
    featured: false,
    benefits: [
      '4 eventos presenciais (todas as edicoes)',
      '12 posts/ano na conta de Antonio',
      'Logo nos materiais e backdrop',
      'Palestra de 10min em 1 evento',
      'Stand compartilhado nos eventos',
      'Mencao em stories quinzenais',
      'Acesso VIP a todas as edicoes',
    ],
  },
  {
    name: 'Prata',
    price: '70',
    color: 'from-stone-300 to-stone-500',
    textColor: 'text-stone-300',
    borderColor: 'border-stone-400/30',
    bgGlow: 'shadow-stone-400/10',
    featured: false,
    benefits: [
      '4 eventos presenciais (todas as edicoes)',
      '6 posts/ano na conta de Antonio',
      'Logo nos materiais digitais',
      'Stand compartilhado em 2 eventos',
      'Mencao em stories mensal',
      'Acesso a area de networking',
    ],
  },
  {
    name: 'Bronze',
    price: '60',
    color: 'from-orange-600 to-orange-800',
    textColor: 'text-orange-400',
    borderColor: 'border-orange-600/30',
    bgGlow: 'shadow-orange-600/10',
    featured: false,
    benefits: [
      '2 eventos presenciais',
      '4 posts/ano na conta de Antonio',
      'Logo nos materiais digitais',
      'Acesso a area de networking',
      '2 ingressos VIP por evento',
    ],
  },
]

const STATS = [
  { value: '500+', label: 'Empresarios por evento' },
  { value: '879k', label: 'Alcance mensal' },
  { value: '6', label: 'Edicoes realizadas' },
  { value: '78%', label: 'Taxa de renovacao' },
]

const ROI_DATA = [
  { metric: 'CPM (Custo por Mil)', ours: 'R$ 7,14', market: 'R$ 15-100', saving: '50-90%' },
  { metric: 'CPC (Custo por Clique)', ours: 'R$ 0,23', market: 'R$ 0,50-5,00', saving: '50-80%' },
  { metric: 'Alcance Mensal', ours: '879.000', market: 'Variavel', saving: 'Garantido' },
  { metric: 'Cliques/Mes', ours: '55.200', market: 'Variavel', saving: 'Comprovado' },
]

export function ParceirosContent({ logoUrl }: { logoUrl?: string }) {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div className="min-h-screen bg-stone-950 font-sans text-white overflow-x-hidden">
      {/* HEADER */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-stone-950/95 backdrop-blur-md border-b border-white/5' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 h-16">
          <Link href="/" className="flex items-center gap-2 shrink-0">
            {logoUrl ? (
              <Image src={logoUrl} alt="eBuz" width={100} height={32} className="h-8 w-auto brightness-200" />
            ) : (
              <span className="font-display text-xl font-bold tracking-tight text-white">eBuz</span>
            )}
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#oportunidade" className="text-sm font-medium text-stone-400 hover:text-white transition-colors">A Oportunidade</a>
            <a href="#cotas" className="text-sm font-medium text-stone-400 hover:text-white transition-colors">Cotas</a>
            <a href="#roi" className="text-sm font-medium text-stone-400 hover:text-white transition-colors">ROI</a>
            <a href="#depoimentos" className="text-sm font-medium text-stone-400 hover:text-white transition-colors">Depoimentos</a>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm rounded-full transition-all shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30 hover:scale-105">
              Agendar Reuniao
            </a>
          </nav>
          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2 text-stone-400" aria-label="Menu">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {menuOpen ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-stone-900/95 backdrop-blur-md border-t border-white/5 px-4 py-3 space-y-1">
            <a href="#oportunidade" onClick={() => setMenuOpen(false)} className="block px-3 py-2 text-sm font-medium text-stone-300 hover:bg-white/5 rounded-lg">A Oportunidade</a>
            <a href="#cotas" onClick={() => setMenuOpen(false)} className="block px-3 py-2 text-sm font-medium text-stone-300 hover:bg-white/5 rounded-lg">Cotas</a>
            <a href="#roi" onClick={() => setMenuOpen(false)} className="block px-3 py-2 text-sm font-medium text-stone-300 hover:bg-white/5 rounded-lg">ROI</a>
            <a href="#depoimentos" onClick={() => setMenuOpen(false)} className="block px-3 py-2 text-sm font-medium text-stone-300 hover:bg-white/5 rounded-lg">Depoimentos</a>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpen(false)} className="block w-full text-center px-3 py-2.5 text-sm font-bold text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors">
              Agendar Reuniao
            </a>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-center pt-16">
        <div className="absolute inset-0">
          <Image
            src="/images/parceiros/hero-encontro.png"
            alt="Encontro de Negocios"
            fill
            className="object-cover object-top"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/80 to-stone-950/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-transparent to-transparent" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-full text-xs font-bold tracking-widest uppercase mb-6 animate-fade-in">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              Cotas de Patrocinio 2025
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-black leading-[1.1] tracking-tight mb-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Sua marca na frente de{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">+500 empresarios</span>{' '}
              decision-makers.
            </h1>
            <p className="text-lg sm:text-xl text-stone-300 mb-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Patrocine o maior Encontro de Negocios da America Latina. 4 eventos presenciais + midia mensal com 879k de alcance. ROI comprovado.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold text-lg rounded-full transition-all shadow-xl shadow-emerald-500/25 hover:shadow-emerald-500/40 hover:scale-105">
                Agendar Reuniao
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </a>
              <a href="#cotas" className="inline-flex items-center gap-2 px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-lg rounded-full transition-all">
                Ver Cotas
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="py-8 border-y border-white/5 bg-stone-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {STATS.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="font-display text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">{stat.value}</div>
                <div className="text-sm text-stone-400 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* A OPORTUNIDADE */}
      <section id="oportunidade" className="py-16 sm:py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-full text-xs font-bold tracking-widest uppercase mb-4">
              A Oportunidade
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-black mb-4">
              Nao e um evento. E um <span className="text-emerald-400">ecossistema</span>.
            </h2>
            <p className="text-lg text-stone-400 max-w-2xl mx-auto">
              Um pacote anual integrado que combina presenca fisica, midia digital e acesso direto a decision-makers.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/[0.03] rounded-2xl p-6 border border-white/5 hover:border-emerald-500/20 transition-colors">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
              </div>
              <h3 className="font-display text-xl font-bold mb-2">4 Eventos Presenciais</h3>
              <p className="text-stone-400">+500 empresarios por edicao. CEOs, proprietarios e gestores com poder de decisao real. Não e público generico.</p>
            </div>

            <div className="bg-white/[0.03] rounded-2xl p-6 border border-white/5 hover:border-emerald-500/20 transition-colors">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
              </div>
              <h3 className="font-display text-xl font-bold mb-2">Midia Mensal (879k Alcance)</h3>
              <p className="text-stone-400">Posts na conta de Antonio Fogaca com 879k de alcance/mes, 55.2k cliques/mes e 32.6k interacoes. Midia contínua, não pontual.</p>
            </div>

            <div className="bg-white/[0.03] rounded-2xl p-6 border border-white/5 hover:border-emerald-500/20 transition-colors">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
              </div>
              <h3 className="font-display text-xl font-bold mb-2">Autoridade Comprovada</h3>
              <p className="text-stone-400">Antonio Fogaca gerencia bilhoes no ecossistema Pablo Marcal. +14 CNPJs, 7+ digitos mensais, 37 anos de experiência operacional.</p>
            </div>

            <div className="bg-white/[0.03] rounded-2xl p-6 border border-white/5 hover:border-emerald-500/20 transition-colors">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
              </div>
              <h3 className="font-display text-xl font-bold mb-2">ROI Documentado</h3>
              <p className="text-stone-400">CPM de R$ 7,14 (mercado: R$ 15-100). CPC de R$ 0,23 (mercado: R$ 0,50-5,00). 50-90% mais barato que publicidade tradicional.</p>
            </div>
          </div>
        </div>
      </section>

      {/* QUEM E ANTONIO */}
      <section className="py-16 sm:py-24 px-4 bg-gradient-to-b from-stone-900/50 to-stone-950">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center">
            <div className="shrink-0">
              <div className="w-48 h-48 sm:w-56 sm:h-56 rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/10 border border-white/10">
                <Image src="/images/parceiros/hero-encontro.png" alt="Antonio Fogaca" width={224} height={224} className="w-full h-full object-cover" />
              </div>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-full text-xs font-bold tracking-widest uppercase mb-4">
                Quem lidera
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-black mb-4">
                Antonio Fogaca
              </h2>
              <p className="text-stone-300 text-lg leading-relaxed mb-6">
                Vice-presidente e CFO do ecossistema Pablo Marcal. Gerencia bilhoes em negocios, possui +14 CNPJs ativos e 37 anos de experiencia transformando negocios sazonais em operacoes anuais.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="bg-white/[0.03] rounded-xl p-3 border border-white/5 text-center">
                  <div className="font-display text-xl font-black text-emerald-400">879k</div>
                  <div className="text-xs text-stone-500">Alcance/mes</div>
                </div>
                <div className="bg-white/[0.03] rounded-xl p-3 border border-white/5 text-center">
                  <div className="font-display text-xl font-black text-emerald-400">55.2k</div>
                  <div className="text-xs text-stone-500">Cliques/mes</div>
                </div>
                <div className="bg-white/[0.03] rounded-xl p-3 border border-white/5 text-center">
                  <div className="font-display text-xl font-black text-emerald-400">14+</div>
                  <div className="text-xs text-stone-500">CNPJs ativos</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COTAS */}
      <section id="cotas" className="py-16 sm:py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-full text-xs font-bold tracking-widest uppercase mb-4">
              Cotas de Patrocinio
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-black mb-4">
              Escolha o nivel de <span className="text-emerald-400">visibilidade</span>
            </h2>
            <p className="text-lg text-stone-400 max-w-xl mx-auto">
              Pacotes anuais com eventos presenciais + midia digital. Preco validado por benchmarks de mercado.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TIERS.map((tier, i) => (
              <div
                key={tier.name}
                className={`relative bg-white/[0.02] rounded-2xl p-6 border ${tier.borderColor} hover:border-opacity-60 transition-all ${tier.featured ? 'ring-1 ring-cyan-500/30 shadow-2xl ' + tier.bgGlow : ''}`}
              >
                {tier.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-cyan-400 to-cyan-600 text-stone-900 text-xs font-bold rounded-full">
                    Mais Popular
                  </div>
                )}
                <div className={`font-display text-2xl font-black ${tier.textColor} mb-1`}>{tier.name}</div>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-sm text-stone-500">R$</span>
                  <span className="font-display text-4xl font-black text-white">{tier.price}</span>
                  <span className="text-sm text-stone-500">mil/ano</span>
                </div>
                <div className="space-y-3 mb-6">
                  {tier.benefits.map((b, j) => (
                    <div key={j} className="flex gap-3 items-start">
                      <svg className={`w-5 h-5 shrink-0 mt-0.5 ${tier.textColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      <span className="text-sm text-stone-300">{b}</span>
                    </div>
                  ))}
                </div>
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block w-full text-center py-3 rounded-xl font-bold text-sm transition-all ${tier.featured ? 'bg-gradient-to-r from-cyan-400 to-cyan-600 text-stone-900 hover:shadow-lg hover:shadow-cyan-500/20' : 'bg-white/5 text-white hover:bg-white/10 border border-white/10'}`}
                >
                  Solicitar Proposta
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI */}
      <section id="roi" className="py-16 sm:py-24 px-4 bg-gradient-to-b from-stone-900/50 to-stone-950">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-full text-xs font-bold tracking-widest uppercase mb-4">
              Retorno Comprovado
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-black mb-4">
              ROI que faz sentido <span className="text-emerald-400">nos números</span>
            </h2>
            <p className="text-lg text-stone-400 max-w-2xl mx-auto">
              Comparativo real: quanto custa no mercado vs quanto custa aqui. Dados baseados em benchmarks de CPM e CPC do mercado brasileiro.
            </p>
          </div>

          <div className="bg-white/[0.02] rounded-2xl border border-white/5 overflow-hidden">
            <div className="grid grid-cols-4 gap-px bg-white/5">
              <div className="bg-stone-950 p-4 text-sm font-bold text-stone-400">Métrica</div>
              <div className="bg-stone-950 p-4 text-sm font-bold text-emerald-400 text-center">Encontro</div>
              <div className="bg-stone-950 p-4 text-sm font-bold text-stone-400 text-center">Mercado</div>
              <div className="bg-stone-950 p-4 text-sm font-bold text-stone-400 text-center">Economia</div>
            </div>
            {ROI_DATA.map((row, i) => (
              <div key={i} className="grid grid-cols-4 gap-px bg-white/5">
                <div className="bg-stone-900/50 p-4 text-sm text-stone-300">{row.metric}</div>
                <div className="bg-stone-900/50 p-4 text-sm text-emerald-400 font-bold text-center">{row.ours}</div>
                <div className="bg-stone-900/50 p-4 text-sm text-stone-400 text-center">{row.market}</div>
                <div className="bg-stone-900/50 p-4 text-sm text-emerald-400 font-bold text-center">{row.saving}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-emerald-500/5 rounded-2xl p-6 border border-emerald-500/10">
            <p className="text-stone-300 text-center">
              <strong className="text-emerald-400">Exemplo Diamante (R$ 150k):</strong> 21 milhoes de impressoes/ano + 662.400 cliques + acesso a 2.000 empresarios. Referência: Afiliados Brasil cobra R$ 120k por cota similar.
            </p>
          </div>
        </div>
      </section>

      {/* DEPOIMENTOS */}
      <section id="depoimentos" className="py-16 sm:py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-full text-xs font-bold tracking-widest uppercase mb-4">
              Depoimentos
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-black mb-4">
              Quem participou, <span className="text-emerald-400">recomenda</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {VIDEO_URLS.map((url, i) => (
              <div key={i} className="bg-white/[0.03] rounded-2xl overflow-hidden border border-white/5 hover:border-emerald-500/20 transition-colors">
                <video
                  controls
                  preload="metadata"
                  className="w-full aspect-[9/16] object-cover bg-stone-900"
                  playsInline
                >
                  <source src={url} type="video/mp4" />
                </video>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENCHMARKS */}
      <section className="py-16 sm:py-24 px-4 bg-gradient-to-b from-stone-900/50 to-stone-950">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-black mb-10">
            Precos validados pelo <span className="text-emerald-400">mercado</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/[0.03] rounded-xl p-5 border border-white/5">
              <div className="text-sm text-stone-500 mb-1">Afiliados Brasil</div>
              <div className="font-display text-2xl font-black text-white">R$ 120k</div>
              <div className="text-xs text-stone-500 mt-1">Cota Master</div>
            </div>
            <div className="bg-white/[0.03] rounded-xl p-5 border border-white/5">
              <div className="text-sm text-stone-500 mb-1">Influencer Mkt</div>
              <div className="font-display text-2xl font-black text-white">5,78x</div>
              <div className="text-xs text-stone-500 mt-1">ROI medio (Later 2025)</div>
            </div>
            <div className="bg-white/[0.03] rounded-xl p-5 border border-white/5">
              <div className="text-sm text-stone-500 mb-1">Taxa Renovacao</div>
              <div className="font-display text-2xl font-black text-white">78%</div>
              <div className="text-xs text-stone-500 mt-1">Texas A&M (5.800 patrocinios)</div>
            </div>
            <div className="bg-white/[0.03] rounded-xl p-5 border border-white/5">
              <div className="text-sm text-stone-500 mb-1">Mercado Global</div>
              <div className="font-display text-2xl font-black text-white">US$ 32B</div>
              <div className="text-xs text-stone-500 mt-1">Patrocinios 2025</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-16 sm:py-24 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/20 via-stone-950 to-cyan-900/20" />
        <div className="relative max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-black leading-tight mb-6">
            Sua marca merece estar na frente de quem{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">decide</span>.
          </h2>
          <p className="text-stone-400 text-lg mb-8 max-w-xl mx-auto">
            Agende uma reuniao estrategica. Apresentamos o media kit completo, cases de sucesso e o plano ideal para sua empresa.
          </p>

          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-10 py-5 bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold text-xl rounded-full transition-all shadow-xl shadow-emerald-500/25 hover:shadow-emerald-500/40 hover:scale-105">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Agendar Reuniao Estrategica
          </a>

          <div className="flex flex-wrap justify-center gap-6 mt-8 text-xs text-stone-500">
            <span>Resposta em ate 24h</span>
            <span>Media kit completo na reuniao</span>
            <span>Sem compromisso</span>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <SiteFooter />

      {/* WhatsApp Floating */}
      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-emerald-500 hover:bg-emerald-600 rounded-full flex items-center justify-center shadow-xl shadow-emerald-500/30 transition-all hover:scale-110"
        aria-label="WhatsApp"
      >
        <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      <style jsx global>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          opacity: 0;
          animation: fade-in 0.6s ease-out forwards;
        }
      `}</style>
    </div>
  )
}
